import React, { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  retryCount: number
  showManualReload: boolean
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    retryCount: 0,
    showManualReload: false
  }

  private MAX_RELOAD_ATTEMPTS = 2
  private RELOAD_ATTEMPT_KEY = 'webpack_reload_attempts'

  private getReloadAttempts = (): number => {
    return parseInt(sessionStorage.getItem(this.RELOAD_ATTEMPT_KEY) || '0', 10)
  }

  private incrementReloadAttempts = (): number => {
    const attempts = this.getReloadAttempts() + 1
    sessionStorage.setItem(this.RELOAD_ATTEMPT_KEY, attempts.toString())
    return attempts
  }

  private clearReloadAttempts = () => {
    sessionStorage.removeItem(this.RELOAD_ATTEMPT_KEY)
  }

  private isSpecificLayoutError = (error: Error): boolean => {
    return error.message.includes('Cannot find module') &&
           (error.message.includes('Layout') &&
            (error.stack?.includes('layout') ?? false))
  }

  public handleRetry = () => {
    if (this.state.retryCount < 1) {
      this.setState({
        hasError: false,
        error: undefined,
        retryCount: this.state.retryCount + 1
      })
    } else {
      window.location.reload()
    }
  }

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      retryCount: 0,
      showManualReload: false
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Erro capturado pelo ErrorBoundary:', error, errorInfo)

    // Check if it's our specific layout module error
    if (this.isSpecificLayoutError(error)) {
      const attempts = this.getReloadAttempts()

      if (attempts < this.MAX_RELOAD_ATTEMPTS) {
        console.warn(`Layout module error detected. Attempt ${attempts + 1}/${this.MAX_RELOAD_ATTEMPTS}`)

        this.incrementReloadAttempts()

        // Auto-reload after a short delay (only for specific layout errors)
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } else {
        console.warn('Max reload attempts reached. Showing manual reload option.')
        this.setState({ showManualReload: true })
      }
    }
  }

  public componentDidMount() {
    // Clear reload attempts on successful mount
    this.clearReloadAttempts()
  }

  public render() {
    if (this.state.hasError) {
      const isLayoutError = this.state.error && this.isSpecificLayoutError(this.state.error)
      const attempts = this.getReloadAttempts()

      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center p-8">
            <div className="w-20 h-20 bg-gradient-to-br from-error-500 to-error-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl text-white">
                {isLayoutError && attempts < this.MAX_RELOAD_ATTEMPTS ? '🔄' : '⚠️'}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-text-primary mb-4">
              {isLayoutError && attempts < this.MAX_RELOAD_ATTEMPTS
                ? 'Recarregando aplicação...'
                : 'Ops! Algo deu errado'
              }
            </h1>
            <p className="text-text-muted mb-6 max-w-md">
              {isLayoutError && attempts < this.MAX_RELOAD_ATTEMPTS
                ? `Detectamos um problema de cache. Recarregando automaticamente... (${attempts + 1}/${this.MAX_RELOAD_ATTEMPTS})`
                : 'Ocorreu um erro inesperado. Por favor, recarregue a página.'
              }
            </p>

            {this.state.showManualReload && (
              <div className="mb-4 p-4 bg-warning-100 border border-warning-300 rounded-lg">
                <p className="text-sm text-warning-700 mb-2">
                  Várias tentativas de recarregamento falharam.
                </p>
              </div>
            )}

            {(!isLayoutError || attempts >= this.MAX_RELOAD_ATTEMPTS) && (
              <div className="space-y-3">
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Recarregar Página
                </button>
                {this.state.retryCount < 1 && (
                  <button
                    onClick={this.handleRetry}
                    className="block mx-auto px-4 py-2 text-sm text-primary-500 hover:text-primary-600 transition-colors"
                  >
                    Tentar Novamente
                  </button>
                )}
              </div>
            )}

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-text-muted">Detalhes do erro (desenvolvimento)</summary>
                <pre className="mt-2 p-4 bg-gray-100 rounded text-xs overflow-auto max-w-md">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary